import React, { Component } from 'react';
import gql from 'graphql-tag';
import { camelCase } from 'lodash';
import { Subscription } from 'react-apollo';
import AuctionEndSoon from './AuctionEndSoon';
import AuctionStart from './AuctionStart';
import CloseAll from './CloseAll';
import DrawingStartSoon from './DrawingStartSoon';
import LocationChanged from './LocationChanged';
import NewOffer from './NewOffer';
import TournamentFinalized from './TournamentFinalized';
import TournamentStartSoon from './TournamentStartSoon';
import WStoreReset from './WStoreReset';
import './DesktopNotificationTray.css';

const LONG_DISMISS_TIMEOUT = 20 * 60 * 1000; // 20 minutes
const SHORT_DISMISS_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const CLEAR_ALL_THRESHOLD = 3;
const NOTIFICATION_RENDER_LIMIT = 2;

class DesktopNotificationTray extends Component {
  state = {
    notifications: [],
    timeouts: {}
  };

  componentWillUnmount() {
    for (let timeout of Object.values(this.state.timeouts)) {
      clearTimeout(timeout);
    }
  }

  addNotification = (
    notificationData,
    notificationComponent,
    notificationProps = {},
    notificationTimeDismiss = LONG_DISMISS_TIMEOUT
  ) =>
    this.setState(state => {
      const newId = state.notifications.length
        ? state.notifications[state.notifications.length - 1].notificationId + 1
        : 1;
      const items = [
        ...state.notifications,
        {
          notificationData,
          notificationComponent,
          notificationId: newId,
          notificationProps
        }
      ];
      items.sort((a, b) => a.notificationId - b.notificationId);
      return {
        notifications: items,
        timeouts: {
          ...state.timeouts,
          [newId]: setTimeout(() => {
            this.close(newId);
          }, notificationTimeDismiss)
        }
      };
    });

  close = id => {
    clearTimeout(this.state.timeouts[id]);

    this.setState(state => {
      const { ...timeouts } = state.timeouts;
      delete timeouts[id];
      return {
        notifications: state.notifications.filter(
          item => item.notificationId !== id
        ),
        timeouts
      };
    });
  };

  closeAll = () => {
    for (let timeout of Object.values(this.state.timeouts)) {
      clearTimeout(timeout);
    }
    this.setState(state => ({
      notifications: [],
      timeouts: {}
    }));
  };

  isLocationPreferred = locations => {
    const { user } = this.props.userData;
    const userPreferredLocation = camelCase(user.preferredLocation);
    return locations.includes(userPreferredLocation);
  };

  getAuctionsNotification = data => {
    if (!this.isLocationPreferred(data.locations)) return null;

    switch (data.event) {
      case 'start':
        return AuctionStart;
      case 'notifyEnd':
        return AuctionEndSoon;
      default:
        return null;
    }
  };

  getDrawingsNotification = data => {
    if (!this.isLocationPreferred(data.locations)) return null;

    switch (data.event) {
      case 'notifyStart':
        return DrawingStartSoon;
      default:
        return null;
    }
  };

  getTournamentsNotification = data => {
    if (data.privateViewing || !this.isLocationPreferred(data.locations))
      return null;

    switch (data.event) {
      case 'notifyStart':
        return TournamentStartSoon;
      default:
        return null;
    }
  };

  render() {
    const { notifications } = this.state;

    return (
      <div className="desktop-notification-tray">
        <Channel
          subscription={SUBSCRIPTION_WSTORE_RESET_NOTIFICATION}
          addNotification={this.addNotification}
          getNotificationComponent={data => {
            return WStoreReset;
          }}
        />
        <Channel
          subscription={SUBSCRIPTION_NEW_OFFER_NOTIFICATION}
          addNotification={this.addNotification}
          getNotificationComponent={data => {
            if (
              data.newOffersAsPatronRewards &&
              data.newOffersAsPatronRewards.length > 0
            ) {
              return NewOffer;
            } else {
              return null;
            }
          }}
          getProps={data => ({ newOffers: data.newOffersAsPatronRewards })}
        />
        <Channel
          subscription={SUBSCRIPTION_TOURNAMENT_EVENTS}
          addNotification={this.addNotification}
          getNotificationComponent={data => {
            if (data.event === 'notifyStart' && data.privateViewing === false) {
              return TournamentStartSoon;
            } else if (
              data.event === 'finalize' &&
              data.privateViewing === false
            ) {
              return TournamentFinalized;
            } else {
              return null;
            }
          }}
          getProps={data => ({
            tournamentId: data.id,
            startTime: data.startTime,
            locations: data.locations
          })}
          dismissTimeout={SHORT_DISMISS_TIMEOUT}
        />
        <TournamentEndChannel
          primarySubscription={SUBSCRIPTION_TOURNAMENT_EVENTS}
          addNotification={this.addNotification}
          getNotificationComponent={data => TournamentFinalized}
          getProps={data => ({
            tournamentId: data.tournamentId,
            totalPlayers: data.totalPlayers,
            leaderBoard: data.leaderBoard,
            score: data.score,
            event: data.event
          })}
          client={this.props.client}
        />
        <Channel
          subscription={SUBSCRIPTION_DRAWING_EVENTS}
          addNotification={this.addNotification}
          getNotificationComponent={data => {
            switch (data.event) {
              case 'notifyStart':
                return DrawingStartSoon;
              default:
                return null;
            }
          }}
          getProps={data => ({ drawingId: data.drawingId, start: data.start })}
          dismissTimeout={SHORT_DISMISS_TIMEOUT}
        />
        <Channel
          subscription={SUBSCRIPTION_AUCTION_EVENTS}
          addNotification={this.addNotification}
          getNotificationComponent={data => {
            switch (data.event) {
              case 'start':
                return AuctionStart;
              case 'notifyEnd':
                return AuctionEndSoon;
              default:
                return null;
            }
          }}
          getProps={data => ({
            auctionId: data.id,
            startTime: data.startTime,
            endTime: data.endTime
          })}
        />
        <Channel
          subscription={SUBSCRIPTION_PREFERRED_LOCATION_CHANGED}
          addNotification={this.addNotification}
          getNotificationComponent={() => {
            return LocationChanged;
          }}
          getProps={data => ({
            newPreferredLocation: data.newPreferredLocation
          })}
        />

        {notifications.length >= CLEAR_ALL_THRESHOLD && (
          <div className="desktop-notification-commands">
            <CloseAll count={notifications.length} onClick={this.closeAll} />
          </div>
        )}
        <div className="desktop-notifications">
          {notifications.map((notification, index) => {
            const {
              notificationId,
              notificationComponent: NotificationComponent,
              notificationProps
            } = notification;

            return (
              index < NOTIFICATION_RENDER_LIMIT && (
                <NotificationComponent
                  key={notificationId}
                  notificationId={notificationId}
                  onClose={this.close}
                  disabled={index > 0}
                  {...notificationProps}
                />
              )
            );
          })}
        </div>
      </div>
    );
  }
}

export default DesktopNotificationTray;

class Channel extends Component {
  render() {
    const {
      subscription,
      addNotification,
      getNotificationComponent,
      getProps,
      dismissTimeout
    } = this.props;
    return (
      <Subscription
        subscription={subscription}
        shouldResubscribe={true}
        onSubscriptionData={({ client, subscriptionData }) => {
          const notificationComponent = getNotificationComponent(
            subscriptionData.data
          );
          if (notificationComponent) {
            addNotification(
              subscriptionData.data,
              notificationComponent,
              getProps(subscriptionData.data),
              dismissTimeout
            );
          }
        }}
      >
        {({ data, loading, error }) => {
          return null;
        }}
      </Subscription>
    );
  }
}

Channel.defaultProps = {
  getProps: () => ({})
};

class TournamentEndChannel extends Component {
  handlePrimarySubscription = async data => {
    const { client } = this.props;
    const tournamentId = data.id;

    if (data.event === 'finalize') {
      try {
        const result = await fetchTournamentUpdates(client, {
          tournament: { id: tournamentId }
        });
        const getProps = data => ({
          tournamentId: data.id,
          name: data.name,
          leaderBoard: data.leaderBoard,
          winningPlaces: data.winningPlaces
        });
        this.props.addNotification(
          result.tournament,
          TournamentFinalized,
          getProps(result.tournament),
          SHORT_DISMISS_TIMEOUT
        );
      } catch (error) {
        console.error('Failed to fetch tournament updates:', error);
      }
    }
  };

  render() {
    const { primarySubscription } = this.props;

    return (
      <Subscription
        subscription={primarySubscription}
        onSubscriptionData={({ subscriptionData }) => {
          this.handlePrimarySubscription(subscriptionData.data);
        }}
      >
        {() => null}
      </Subscription>
    );
  }
}

TournamentEndChannel.defaultProps = {
  getProps: () => ({})
};

const SUBSCRIPTION_WSTORE_RESET_NOTIFICATION = gql`
  subscription {
    wstoreResetNotification {
      reset
    }
  }
`;

const SUBSCRIPTION_NEW_OFFER_NOTIFICATION = gql`
  subscription {
    newOffers {
      newOffersAsPatronRewards {
        prizeCode
        onlineOfferUuid
        startDate
        endDate
      }
    }
  }
`;

const SUBSCRIPTION_TOURNAMENT_EVENTS = gql`
  subscription TournamentEvents {
    tournamentEvents {
      id
      event
      privateViewing
      startTime
      locations
    }
  }
`;

const SUBSCRIPTION_DRAWING_EVENTS = gql`
  subscription AllDrawingsUpdates {
    allDrawingsUpdates {
      drawingId
      name
      start
      event
    }
  }
`;

const SUBSCRIPTION_AUCTION_EVENTS = gql`
  subscription AuctionEvent {
    auctionEvents {
      id
      event
      name
      description
      startTime
      endTime
    }
  }
`;

export const SUBSCRIPTION_PREFERRED_LOCATION_CHANGED = gql`
  subscription UserPreferredLocationChanged {
    userPreferredLocationChanged {
      newPreferredLocation
    }
  }
`;

const QUERY_GET_TOURNAMENT = gql`
  query GetTournament($tournament: GetTournament!) {
    tournament(tournament: $tournament) {
      id
      name
      winningPlaces {
        winningPlaces {
          place
        }
      }
      leaderBoard {
        rankings {
          player {
            userId
          }
          rank
        }
      }
    }
  }
`;

const fetchTournamentUpdates = async (client, variables) => {
  try {
    const { data } = await client.query({
      query: QUERY_GET_TOURNAMENT,
      variables
    });
    return data;
  } catch (error) {
    throw error;
  }
};
